import React, { useState } from 'react';
import { Menu, X, ChevronRight, Download, FileText } from 'lucide-react';
import { Button } from '../ui/button';
import { API } from './shared';

const Navigation = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const isHome = typeof window !== 'undefined' && window.location.pathname === '/';

  const anchor = (hash) => isHome ? hash : `/${hash}`;

  const navLinks = [
    { label: 'Challenges We Solve', href: anchor('#challenges') },
    { label: 'Capabilities', href: anchor('#capabilities') },
    { label: 'Evidence', href: anchor('#evidence') },
    { label: 'Leadership', href: anchor('#leadership') },
    { label: 'Partner Firms', href: '/partners' },
    { label: 'Newsletter', href: '/newsletter' },
    { label: 'Media', href: '/media' },
  ];

  const resources = [
    { label: 'One-Pager Overview (PDF)', href: `${API}/resources/apig-one-pager-new.pdf`, icon: Download, external: true },
    { label: 'APIG Firm Overview (PDF)', href: `${API}/resources/apig-firm-overview.pdf`, icon: Download, external: true },
    { label: 'ASEMAP Advantage Explained (PDF)', href: `${API}/resources/asemap-advantage.pdf`, icon: Download, external: true },
    { label: 'Full Capabilities Deck (PDF)', href: `${API}/resources/apig-full-capabilities.pdf`, icon: Download, external: true },
    { label: 'Case Evidence Library', href: '/evidence', icon: FileText, external: false },
  ];

  const handleCtaClick = () => {
    if (isHome) {
      document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
    } else {
      window.location.href = '/#contact';
    }
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 nav-glass" data-testid="main-navigation">
      <div className="container-luxury">
        <div className="flex items-center justify-between h-20">
          <a href="/" className="flex items-center gap-2 flex-shrink-0 mr-6" data-testid="nav-logo">
            <img src={`${API}/resources/apig-logo-dark.png`} alt="APIG Alliance" className="h-12" />
          </a>

          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <a key={link.label} href={link.href} className="nav-link" data-testid={`nav-link-${link.label.toLowerCase()}`}>
                {link.label}
              </a>
            ))}
            
            <div className="relative">
              <button
                onClick={() => setResourcesOpen(!resourcesOpen)}
                onBlur={() => setTimeout(() => setResourcesOpen(false), 200)}
                className="nav-link flex items-center gap-1"
                data-testid="nav-resources-toggle"
              >
                Resources
                <ChevronRight className={`w-3 h-3 transition-transform ${resourcesOpen ? 'rotate-90' : ''}`} />
              </button>
              {resourcesOpen && (
                <div className="absolute top-full right-0 mt-2 bg-white border border-[#E2E8F0] shadow-lg min-w-[200px]">
                  {resources.map((resource) => (
                    <a
                      key={resource.label}
                      href={resource.href}
                      {...(resource.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                      className="flex items-center gap-3 px-4 py-3 text-sm text-[#334155] hover:bg-[#F1F5F9] hover:text-[#D97706] transition-colors"
                      data-testid={`resource-${resource.label.toLowerCase().replace(/\s/g, '-')}`}
                    >
                      <resource.icon className="w-4 h-4" />
                      {resource.label}
                    </a>
                  ))}
                </div>
              )}
            </div>
            
            <Button 
              className="btn-primary rounded-none"
              data-testid="nav-cta-button"
              onClick={handleCtaClick}
            >
              Submit Your Challenge
            </Button>
          </div>

          <button
            className="lg:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-[#E2E8F0] bg-white/95 backdrop-blur-md" data-testid="mobile-menu">
            <div className="py-4 space-y-1">
              {navLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  className="block px-4 py-3 text-sm font-medium text-[#334155] hover:text-[#D97706] hover:bg-[#F1F5F9] transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.label}
                </a>
              ))}
              {resources.map((resource) => (
                <a
                  key={resource.label}
                  href={resource.href}
                  {...(resource.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                  className="block px-4 py-3 text-sm font-medium text-[#334155] hover:text-[#D97706] hover:bg-[#F1F5F9] transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {resource.label}
                </a>
              ))}
              <div className="px-4 pt-2">
                <Button
                  className="btn-primary rounded-none w-full"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    handleCtaClick();
                  }}
                >
                  Submit Your Challenge
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
